import type { ChecklistDto } from '@iac/contracts';
import { Link, useParams } from 'react-router-dom';
import { Checklist } from '../../components/Checklist';
import { EmptyState } from '../../components/EmptyState';
import { IconChevronLeft, IconPencilSquare } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { NotFound } from '../../routes/NotFound';
import { useChecklists, useResetChecklist, useSetItem } from './api';
import { checklistsDict } from './i18n';
import { childrenOf, progress, type StandardChecklist } from './lists';
import { ResetButton } from './ResetButton';

/**
 * The list the URL names, with every list for resolving children; `null` when
 * it is not there, `undefined` while loading.
 */
export function useChecklistFromUrl():
  { list: ChecklistDto; all: ChecklistDto[] } | null | undefined {
  const { id } = useParams();
  const { data: lists } = useChecklists();
  if (!lists) return undefined;
  const list = lists.find((l) => l.id === id);
  return list ? { list, all: lists } : null;
}

export function ChecklistView() {
  const found = useChecklistFromUrl();
  if (found === undefined) return null;
  if (found === null) return <NotFound />;
  return <Ticking list={found.list} all={found.all} />;
}

function Ticking({ list, all }: { list: ChecklistDto; all: ChecklistDto[] }) {
  const t = useDict(checklistsDict);
  usePageTitle(`${list.name} — ${t.titleSuffix}`);
  const reset = useResetChecklist();
  const { done, total } = progress(list, all);
  const children = childrenOf(list, all);
  const editTo = `/checklists/${list.id}/edit`;

  return (
    <>
      <Link to="/checklists" className="text-link checklist-back">
        <IconChevronLeft />
        {t.back}
      </Link>
      <PageHeader
        title={list.name}
        actions={
          <div className="checklist-actions">
            <ResetButton
              label={t.reset}
              confirm={
                list.kind === 'combined'
                  ? t.confirmResetCombined(
                      list.name,
                      children.map((c) => c.name),
                    )
                  : t.confirmReset(list.name)
              }
              disabled={done === 0}
              onConfirm={() => reset.mutate(list.id)}
            />
            <Link
              to={editTo}
              className="btn-icon-ghost"
              aria-label={t.edit}
              title={t.edit}
            >
              <IconPencilSquare />
            </Link>
          </div>
        }
      />
      <p className="checklist-progress">{t.progressLabel(done, total)}</p>

      {list.kind === 'standard' ? (
        list.items.length > 0 ? (
          <Panel>
            <Items list={list} />
          </Panel>
        ) : (
          <EmptyState size="lg">
            {t.emptyStandard}{' '}
            <Link to={editTo} className="text-link">
              {t.addItems}
            </Link>
          </EmptyState>
        )
      ) : children.length > 0 ? (
        children.map((child) => (
          <Panel key={child.id} title={child.name}>
            {child.items.length > 0 ? (
              <Items list={child} />
            ) : (
              <p className="text-hint">{t.emptyChild}</p>
            )}
          </Panel>
        ))
      ) : (
        <EmptyState size="lg">
          {t.emptyCombined}{' '}
          <Link to={editTo} className="text-link">
            {t.pickLists}
          </Link>
        </EmptyState>
      )}
    </>
  );
}

/** A standard list's items; a tick is saved on the item, wherever it shows. */
function Items({ list }: { list: StandardChecklist }) {
  const setItem = useSetItem();
  return (
    <Checklist
      items={list.items.map((i) => i.text)}
      checked={list.items.map((i) => i.checked)}
      onToggle={(index) => {
        const item = list.items[index];
        if (item)
          setItem.mutate({
            listId: list.id,
            itemId: item.id,
            checked: !item.checked,
          });
      }}
    />
  );
}
